import axios from "axios";
import { getPathDatabaseByRole, renewToken } from "./auth";
import { LaunchError } from "../utils/errors";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import types from "../types";
import {
  get,
  ref,
  set,
  query,
  orderByChild,
  startAt,
  endAt,
} from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import subMonths from "date-fns/subMonths";
import format from "date-fns/format";
import { db } from "../lib/firebase";
import { openLink, getLanguageFormatted } from "../utils/string";
import i18next from "i18next";
import {
  PLAN_BASIC,
  PLAN_STARTER,
  PLAN_STUDIO,
  PLAN_AGENCY,
  PLAN_PRO,
  PLAN_ADVANCED,
  PLAN_TYPE_PACKAGES,
  RECURRENT_MULTIPLIER,
} from "../utils/constants";
import {
  buildAdsCampaignActionCostsByType,
  DEFAULT_CREDIT_ACTION_COSTS,
  DEFAULT_CREDIT_PRICING_VERSION,
  resolveActionLogCreditsSpent,
  shouldIncludeActionLogInCreditsUsage,
} from "../utils/creditActionCosts";

export const startCreateStripeSource =
  ({ paymentMethodID, setDefault = false, activatePlan = true }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const body = {
        paymentMethodID,
        setDefault,
        activatePlan,
      };

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createStripeSource")}/createStripeSource`,
        data: body,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok) {
        setDefault && dispatch(startSetStripeSourceDefault(data.data));
        setDefault && dispatch(setStripeCustomerID(data.data.customerID));
      }
      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateStripeSource",
        })
      );
      return { ok: false };
    }
  };

export const startVerifySubscriptionCustomer =
  ({ paymentMethodID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("verifySubscriptionCustomer")}/verifySubscriptionCustomer`,
        data: { paymentMethodID },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok) {
        dispatch(startSetStripeSourceDefault(data.data));
        dispatch(setStripeCustomerID(data.data.customerID));
      }
      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startVerifySubscriptionCustomer",
        })
      );
      return {
        ok: false,
        message: error.response?.data?.message || error.message,
      };
    }
  };

export const startCancelSubscription = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("cancelSubscriptionStripe")}/cancelSubscriptionStripe`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!data.ok) {
      return { ok: false };
    }

    const cancelDateTime = data?.data?.cancelDateTime;
    dispatch(cancelSubscription(cancelDateTime));

    return { ok: true, cancelDateTime };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startCancelSubscription",
      })
    );
    return { ok: false };
  }
};
const cancelSubscription = (data) => ({
  type: types.CANCEL_SUBSCRIPTION,
  payload: data,
});

export const startCancelReason = (cancellationData) => async (dispatch) => {
  try {
    const { pathSubscriptionPlanInfo } = dispatch(getPathDatabaseByRole());
    const cancelReasonRef = ref(db, `${pathSubscriptionPlanInfo}/cancelReason`);
    const newReason = {
      reason: cancellationData.reason,
      selectedReason: cancellationData.selectedReason,
      cancelDateTime: new Date().getTime(),
    };
    await set(cancelReasonRef, newReason);
    return { ok: true, data: newReason };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startCancelReason",
      })
    );
    return { ok: false };
  }
};

export const startReactivateSubscription = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("reactiveSubscriptionStripe")}/reactiveSubscriptionStripe`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!data.ok) {
      return { ok: false };
    }

    dispatch(reactivateSubscription());

    return { ok: true };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startReactivateSubscription",
      })
    );
    return { ok: false };
  }
};
const reactivateSubscription = () => ({
  type: types.REACTIVATE_SUBSCRIPTION,
});

export const startSetStripeSourceDefault = (cardData) => async (dispatch) => {
  try {
    dispatch(setStripeSourceDefault(cardData));
    return true;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startSetStripeSourceDefault",
      })
    );
    return false;
  }
};

const setStripeSourceDefault = (data) => ({
  type: types.SET_DEFAULT_PAYMENT_METHOD,
  payload: data,
});
const setStripeCustomerID = (data) => ({
  type: types.SET_CUSTOMER_ID,
  payload: data,
});

export const startDeleteStripeSource =
  ({ paymentMethodID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const body = {
        paymentMethodID,
      };

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("deleteStripeSource")}/deleteStripeSource`,
        data: body,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDeleteStripeSource",
        })
      );
      return false;
    }
  };

export const startGetStripeSources = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("getStripeSources")}/getStripeSources`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetStripeSources",
      })
    );
    return false;
  }
};

export const startMakeStripeSourceDefault =
  ({ paymentMethodID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const body = {
        paymentMethodID,
      };

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("makeStripeSourceDefault")}/makeStripeSourceDefault`,
        data: body,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startMakeStripeSourceDefault",
        })
      );
      return false;
    }
  };

export const startGetStripeInfo = () => async (dispatch) => {
  try {
    const { pathBase } = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, `${pathBase}/stripe`);

    const snapshot = await get(dbRef);

    if (snapshot.exists()) {
      const data = snapshot.val();
      dispatch(getStripeInfo(data));
      return data;
    }
    dispatch(loadingFinishPayments());

    return true;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetStripeInfo",
      })
    );
    return false;
  }
};

const getStripeInfo = (data) => ({
  type: types.GET_STRIPE_INFO_FINISH,
  payload: data,
});

export const startPayInvoice =
  ({ invoiceID, paymentMethodID, hasSingleCharge = true, paymentIntent }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("payInvoice")}/payInvoice`,
        data: {
          invoiceID,
          paymentMethodID,
          hasSingleCharge,
          paymentIntent,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (data.ok) {
        dispatch(
          payInvoice({
            invoiceID,
            paymentID: data.data.paymentID,
            invoiceIDStripe: data.data.invoiceID,
            paidDate: data.data.paidDate,
          })
        );
        return { ok: true };
      }
      if (!data.ok && data.status) {
        return {
          ok: false,
          clientSecret: data.data.clientSecret,
          status: data.status,
        };
      }
      return { ok: false };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startPayInvoice",
        })
      );
      return false;
    }
  };
const payInvoice = (data) => ({
  type: types.PAY_INVOICE,
  payload: data,
});

export const startGetPayments =
  ({ onCallbackFinish = () => {} }) =>
  async (dispatch) => {
    try {
      const { pathPayments } = dispatch(getPathDatabaseByRole());
      const snapshot = await get(ref(db, pathPayments));
      if (snapshot.exists()) {
        const payments = snapshot.val();
        dispatch(getPayments(payments));
        onCallbackFinish();
        return true;
      } else {
        dispatch(loadingFinishPayments());
        onCallbackFinish();
        return false;
      }
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetPayments",
        })
      );
      onCallbackFinish();
      return false;
    }
  };
const getPayments = (payments) => ({
  type: types.GET_PAYMENTS_FINISH,
  payload: payments,
});

export const startGetPlansAds = () => async (dispatch, getState) => {
  try {
    const user = getState().auth.user;

    let path;
    if (!user) {
      path = ref(db, `adsPlans/${DEFAULT_CREDIT_PRICING_VERSION}`);
    } else {
      const { pathAdsPlans } = dispatch(getPathDatabaseByRole());
      path = ref(db, pathAdsPlans);
    }

    const snapshot = await get(path);
    if (snapshot.exists()) {
      const plans = snapshot.val();
      dispatch(getPlans(plans));
      return true;
    }
    return false;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetPlansAds",
      })
    );
    return false;
  }
};
const getPlans = (plans) => ({
  type: types.GET_PLANS_FINISH,
  payload: plans,
});
export const startGetSubscriptionPlan = () => async (dispatch) => {
  try {
    const { pathSubscriptionPlan } = dispatch(getPathDatabaseByRole());

    const dbRef = ref(db, pathSubscriptionPlan);

    const snapshot = await get(dbRef);

    if (snapshot.exists()) {
      const subscriptionPlan = snapshot.val();
      dispatch(getSubscriptionPlan(subscriptionPlan));
      return subscriptionPlan;
    }
    return false;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetSubscriptionPlan",
      })
    );
    return false;
  }
};
const getSubscriptionPlan = (subscriptionPlan) => ({
  type: types.GET_SUBSCRIPTION_PLAN,
  payload: subscriptionPlan,
});
export const startGetIsAppSumo = () => async (dispatch) => {
  try {
    const { agencyID } = dispatch(getPathDatabaseByRole());

    const dbRef = ref(db, `agencies/${agencyID}/isAppSumo`);

    const snapshot = await get(dbRef);

    if (snapshot.exists()) {
      const isAppSumo = snapshot.val();
      return isAppSumo;
    }
    return false;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetSubscriptionPlan",
      })
    );
    return false;
  }
};
export const startGetSubscriptionPlanInfo = () => async (dispatch) => {
  try {
    const { pathSubscriptionPlanInfo } = dispatch(getPathDatabaseByRole());

    const dbRef = ref(db, pathSubscriptionPlanInfo);

    const snapshot = await get(dbRef);

    if (snapshot.exists()) {
      const subscriptionPlanInfo = snapshot.val();
      dispatch(getSubscriptionPlanInfo(subscriptionPlanInfo));
      return true;
    }
    return false;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetSubscriptionPlanInfo",
      })
    );
    return false;
  }
};
export const startListenerSubscriptionPlanInfo =
  (onReferenceAvailable = () => {}) =>
  async (dispatch) => {
    try {
      const { pathSubscriptionPlanInfo } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(db, pathSubscriptionPlanInfo);

      onValue(dbRef, (snapshot) => {
        const data = snapshot.val();
        dispatch(getSubscriptionPlanInfo(data));
      });
      onReferenceAvailable(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startListenerSubscriptionPlanInfo",
        })
      );
      return false;
    }
  };
const getSubscriptionPlanInfo = (subscriptionPlanInfo) => ({
  type: types.GET_SUBSCRIPTION_PLAN_INFO,
  payload: subscriptionPlanInfo,
});

export const startDowngradeSubscriptionForRetention =
  () => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "downgradeSubscriptionForRetention"
        )}/downgradeSubscriptionForRetention`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      await dispatch(startGetSubscriptionPlan());
      await dispatch(startGetSubscriptionPlanInfo());

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDowngradeSubscriptionForRetention",
        })
      );
      return { ok: false };
    }
  };

export const startGetSubscriptionPlanInfoUsage =
  (onReferenceAvailable = () => {}) =>
  async (dispatch) => {
    try {
      const { pathSubscriptionPlanInfoUsage } = dispatch(
        getPathDatabaseByRole()
      );

      const dbRef = ref(db, pathSubscriptionPlanInfoUsage);

      onValue(dbRef, (snapshot) => {
        const data = snapshot.val();
        dispatch(getSubscriptionPlanInfoUsage(data));
      });
      onReferenceAvailable(dbRef);
      return false;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetSubscriptionPlanInfoUsage",
        })
      );
      return false;
    }
  };
const getSubscriptionPlanInfoUsage = (subscriptionPlanInfoUsage) => ({
  type: types.GET_SUBSCRIPTION_PLAN_INFO_USAGE,
  payload: subscriptionPlanInfoUsage,
});

export const startListenerBrandUsageAllocation =
  (onReferenceAvailable = () => {}) =>
  async (dispatch) => {
    try {
      const { pathUsageAllocation } = dispatch(getPathDatabaseByRole());
      if (!pathUsageAllocation) {
        return false;
      }

      const dbRef = ref(db, pathUsageAllocation);
      onValue(dbRef, (snapshot) => {
        dispatch(getBrandUsageAllocation(snapshot.val()));
      });
      onReferenceAvailable(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerBrandUsageAllocation",
        })
      );
      return false;
    }
  };

const getBrandUsageAllocation = (brandUsageAllocation) => ({
  type: types.GET_BRAND_USAGE_ALLOCATION,
  payload: brandUsageAllocation,
});

export const startListenerBrandUsageConsumed =
  (onReferenceAvailable = () => {}) =>
  async (dispatch) => {
    try {
      const { pathUsageConsumed } = dispatch(getPathDatabaseByRole());
      if (!pathUsageConsumed) {
        return false;
      }

      const dbRef = ref(db, pathUsageConsumed);
      onValue(dbRef, (snapshot) => {
        dispatch(getBrandUsageConsumed(snapshot.val()));
      });
      onReferenceAvailable(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerBrandUsageConsumed",
        })
      );
      return false;
    }
  };

const getBrandUsageConsumed = (brandUsageConsumed) => ({
  type: types.GET_BRAND_USAGE_CONSUMED,
  payload: brandUsageConsumed,
});

export const startListenerIsActivePlan =
  (onReferenceAvailable) => async (dispatch) => {
    try {
      const { pathIsPlanActive } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(db, pathIsPlanActive);
      onValue(dbRef, (snapshot) => {
        dispatch(setIsActivePlan(snapshot.val() === true));
      });
      onReferenceAvailable(dbRef);
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startListenerIsActivePlan",
        })
      );
      return false;
    }
  };
export const setIsActivePlan = (isActive) => ({
  type: types.SET_IS_ACTIVE_PLAN,
  payload: isActive,
});

export const setIsPlanActiveResolved = (resolved) => ({
  type: types.SET_IS_PLAN_ACTIVE_RESOLVED,
  payload: resolved,
});
const setTypeOfPlans = (typeOfPlans) => ({
  type: types.SET_TYPE_OF_PLANS,
  payload: typeOfPlans,
});

export const startGetIsActivePlan = () => async (dispatch) => {
  try {
    const { pathIsPlanActive, userID } = dispatch(getPathDatabaseByRole());
    if (!userID) {
      return null;
    }

    const dbRef = ref(db, pathIsPlanActive);
    const snapshot = await get(dbRef);
    const isPlanActive = snapshot.val() === true;
    dispatch(setIsActivePlan(isPlanActive));
    return isPlanActive;
  } catch (error) {
    dispatch(LaunchError(error));
    return null;
  }
};

export const startGetTypeOfPlans = () => async (dispatch) => {
  try {
    const { pathTypeOfPlans } = dispatch(getPathDatabaseByRole());

    const dbRef = ref(db, pathTypeOfPlans);
    const snapshot = await get(dbRef);

    if (snapshot.exists()) {
      const typeOfPlans = snapshot.val();
      dispatch(setTypeOfPlans(typeOfPlans));
      return typeOfPlans;
    }
    dispatch(loadingFinishPayments());
    return null;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetTypeOfPlans",
      })
    );
    return null;
  }
};

const setLinkedInPostShared = (isLinkedInPostShared) => ({
  type: types.SET_LINKEDIN_POST_SHARED,
  payload: isLinkedInPostShared,
});

export const startGetLinkedInPostShared = () => async (dispatch) => {
  try {
    const { pathLinkedInPostShared } = dispatch(getPathDatabaseByRole());
    const dbRef = ref(db, pathLinkedInPostShared);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const isLinkedInPostShared = snapshot.val();
      dispatch(setLinkedInPostShared(isLinkedInPostShared));
      return isLinkedInPostShared;
    }
    return null;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetLinkedInPostShared",
      })
    );
    return null;
  }
};

export const startPaySubscriptionAds =
  ({ planID, recurrent, paymentMethodID, paymentIntent = "", discountID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("paySubscriptionAds")}/paySubscriptionAds`,
        data: {
          planID,
          recurrent,
          paymentMethodID,
          paymentIntent,
          discountID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (data.ok) {
        dispatch(
          paySubscriptionAds({
            planID,
            recurrent,
          })
        );
        return { ok: true };
      }
      if (!data.ok && data.status === "requiresAction") {
        return {
          ok: false,
          clientSecret: data.data.clientSecret,
          status: data.status,
        };
      }
      return {
        ok: false,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startPaySubscriptionAds",
        })
      );
      return {
        ok: false,
      };
    }
  };
export const startChangeSubscriptionPlanStripe =
  ({ planID, paymentIntent }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("changeSubscriptionAdsPlanStripe")}/changeSubscriptionAdsPlanStripe`,
        data: {
          planID,
          paymentIntent,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok) {
        dispatch(
          changeSubscriptionPlanStripe({
            planID,
          })
        );

        return { ok: true };
      }

      if (!data.ok && data.status === "requiresAction") {
        return {
          ok: false,
          clientSecret: data.data.clientSecret,
          status: data.status,
        };
      }

      return {
        ok: false,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startChangeSubscriptionPlanStripe",
        })
      );
      return {
        ok: false,
      };
    }
  };

const changeSubscriptionPlanStripe = (data) => ({
  type: types.CHANGE_SUBSCRIPTION_ADS,
  payload: data,
});

export const startGetPreviewProrationAdsSubscription =
  () => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getPreviewProrationAdsSubscription")}/getPreviewProrationAdsSubscription`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (data.ok) {
        dispatch(setProratedPricePlans(data.data));
        return {
          ok: true,
          data: data.data,
        };
      }
      return data.ok;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetPreviewProrationAdsSubscription",
        })
      );
      return {
        ok: false,
      };
    }
  };
const setProratedPricePlans = (data) => ({
  type: types.SET_PRORATED_PRICE_PLANS,
  payload: data,
});
export const startGetInvoiceUrl =
  ({ paymentID }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getInvoiceUrl")}/getInvoiceUrl`,
        data: {
          paymentID,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (data.ok) {
        openLink(data.data.url);
      }
      return data.ok;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetInvoiceUrl",
        })
      );
      return {
        ok: false,
      };
    }
  };

const paySubscriptionAds = (data) => ({
  type: types.PAY_SUBSCRIPTION_ADS,
  payload: data,
});

export const startGetDiscountCode =
  ({ discountCode }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getDiscountCode")}/getDiscountCode`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          discountCode,
        },
      });

      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetDiscountCode",
        })
      );
      return { ok: false };
    }
  };

export const startCreateUpdatePaymentMethodLinkAds = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("createUpdatePaymentMethodLinkAds")}/createUpdatePaymentMethodLinkAds`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (data.ok) {
      return {
        ok: true,
        data: data.data,
      };
    }

    return {
      ok: false,
    };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startCreateUpdatePaymentMethodLinkAds",
      })
    );
    return {
      ok: false,
    };
  }
};

const loadingFinishPayments = () => ({
  type: types.LOADING_PAYMENTS_FINISH,
});

export const startCreatePurchaseCreditsPaymentLink =
  ({ quantity, purchaseKind = "credits" }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const language = getLanguageFormatted(i18next.language);

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createPurchaseCreditsPaymentLink")}/createPurchaseCreditsPaymentLink`,
        data: {
          quantity,
          language,
          purchaseKind,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok) {
        return {
          ok: true,
          data: data.data,
        };
      }

      return {
        ok: false,
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreatePurchaseCreditsPaymentLink",
        })
      );
      return {
        ok: false,
      };
    }
  };

export const startCreateAddonSpecialistPaymentLink = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());
    const language = getLanguageFormatted(i18next.language);

    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("createAddonSpecialistPaymentLink")}/createAddonSpecialistPaymentLink`,
      data: {
        redirectUrl: window.location.origin,
        language,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (data.ok) {
      return {
        ok: true,
        data: data.data,
      };
    }

    return {
      ok: false,
    };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startCreateAddonSpecialistPaymentLink",
      })
    );
    return {
      ok: false,
    };
  }
};

export const startGetPayInvoiceUrl =
  ({ paymentIntent, coldBoot = false }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getPayInvoiceUrl")}/getPayInvoiceUrl`,
        data: { paymentIntent, coldBoot },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (coldBoot) {
        return {
          ok: true,
        };
      }

      if (data.ok) {
        return {
          ok: true,
          data: data.data,
        };
      }

      return {
        ok: false,
      };
    } catch (error) {
      if (!coldBoot) {
        dispatch(
          LaunchError(error, true, [], {
            functionName: "startGetPayInvoiceUrl",
          })
        );
      }
      return {
        ok: false,
      };
    }
  };

const setLoadingProrationPrices = (isLoading) => ({
  type: types.SET_LOADING_PRORATION_PRICES,
  payload: isLoading,
});

const setProrationPrices = (prices) => ({
  type: types.SET_PRORATION_PRICES,
  payload: prices,
});

const mapEndpointPricesToPlans = (endpointData) => {
  const prorationPrices = {};
  const priceMapping = {
    starter: PLAN_STARTER,
    studio: PLAN_STUDIO,
    agency: PLAN_AGENCY,
    pro: PLAN_PRO,
    advanced: PLAN_ADVANCED,
    basic: PLAN_BASIC,
  };

  Object.keys(priceMapping).forEach((endpointKey) => {
    if (endpointData[endpointKey]) {
      prorationPrices[priceMapping[endpointKey]] = {
        price: endpointData[endpointKey].price,
        currency: endpointData[endpointKey].currency,
      };
    }
  });

  return prorationPrices;
};

export const startGetPreviewProrationAdsSubscriptionSimple =
  () => async (dispatch) => {
    try {
      dispatch(setLoadingProrationPrices(true));

      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("getPreviewProrationAdsSubscription")}/getPreviewProrationAdsSubscription`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data.ok && data.data) {
        console.log("data", JSON.stringify(data.data, null, 2));
        const prorationPrices = mapEndpointPricesToPlans(data.data);

        dispatch(setProrationPrices(prorationPrices));
        dispatch(setLoadingProrationPrices(false));

        return {
          ok: true,
          data: prorationPrices,
        };
      }

      dispatch(setLoadingProrationPrices(false));
      return { ok: false };
    } catch (error) {
      dispatch(setLoadingProrationPrices(false));
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetPreviewProrationAdsSubscriptionSimple",
        })
      );
      return { ok: false };
    }
  };

export const clearProrationPrices = () => (dispatch) => {
  dispatch({
    type: types.CLEAR_PRORATION_PRICES,
  });
};

const VIDEOS_METER_KEY = "videos";

const resolveCreditsUsagePeriodBounds = ({ renewDate, recurrent }) => {
  const recurrentMonths = RECURRENT_MULTIPLIER[recurrent] || 1;
  const now = new Date();

  if (!renewDate) {
    return {
      from: subMonths(now, recurrentMonths).getTime(),
      to: now.getTime(),
    };
  }

  const renew = new Date(renewDate);
  const periodEnd = renew.getTime() > now.getTime() ? renew : now;

  return {
    from: subMonths(periodEnd, recurrentMonths).getTime(),
    to: periodEnd.getTime(),
  };
};

const normalizeAdvertisersByAgencyIds = (raw) => {
  if (Array.isArray(raw)) {
    return raw.filter(Boolean);
  }
  if (raw && typeof raw === "object") {
    return Object.keys(raw);
  }
  return [];
};

const buildAgencyCreditsUsageBreakdown = async ({
  agencyID,
  from,
  to,
  typeOfPlans,
  adsCampaignActionCostsByType,
}) => {
  const [snapshot, advertisersByAgencySnap, usersSnap] = await Promise.all([
    get(
      query(
        ref(db, `actionLogs/${agencyID}`),
        orderByChild("creationTime"),
        startAt(from),
        endAt(to)
      )
    ),
    get(ref(db, `advertisersByAgency/${agencyID}`)),
    get(ref(db, `adsUsers/${agencyID}`)),
  ]);

  const allowedAdvertiserSet = new Set(
    normalizeAdvertisersByAgencyIds(advertisersByAgencySnap.val())
  );

  const usersMap = {};
  if (usersSnap.exists()) {
    usersSnap.forEach((child) => {
      const data = child.val();
      if (data?.isDeleted) return;
      usersMap[child.key] = data?.email || child.key;
    });
  }
  const allowedUserSet = new Set(Object.keys(usersMap));

  const advertiserCredits = {};
  const userCredits = {};
  const advertiserVideos = {};
  const userVideos = {};

  if (snapshot.exists()) {
    snapshot.forEach((child) => {
      const log = child.val();
      const { advertiserID, userID, action } = log;
      const actionConfig = adsCampaignActionCostsByType?.[action];

      if (
        log.meterKey === VIDEOS_METER_KEY ||
        log.usageKey === VIDEOS_METER_KEY
      ) {
        const videos = Number.isFinite(log.totalCost)
          ? log.totalCost
          : log.multiplier || 1;
        if (
          advertiserID &&
          (allowedAdvertiserSet.size === 0 ||
            allowedAdvertiserSet.has(advertiserID))
        ) {
          advertiserVideos[advertiserID] =
            (advertiserVideos[advertiserID] || 0) + videos;
        }
        if (userID && allowedUserSet.has(userID)) {
          userVideos[userID] = (userVideos[userID] || 0) + videos;
        }
        return;
      }

      if (
        !shouldIncludeActionLogInCreditsUsage(log, actionConfig, typeOfPlans)
      ) {
        return;
      }

      const spent = resolveActionLogCreditsSpent(
        log,
        actionConfig,
        typeOfPlans
      );

      if (
        advertiserID &&
        (allowedAdvertiserSet.size === 0 ||
          allowedAdvertiserSet.has(advertiserID))
      ) {
        advertiserCredits[advertiserID] =
          (advertiserCredits[advertiserID] || 0) + spent;
      }
      if (userID && allowedUserSet.has(userID)) {
        userCredits[userID] = (userCredits[userID] || 0) + spent;
      }
    });
  }

  const advertiserIDs = [
    ...new Set([
      ...Object.keys(advertiserCredits).filter(
        (id) => advertiserCredits[id] > 0
      ),
      ...Object.keys(advertiserVideos).filter((id) => advertiserVideos[id] > 0),
    ]),
  ];
  const userIDs = Object.keys(usersMap);

  const businessNamesMap =
    advertiserIDs.length > 0
      ? await Promise.all(
          advertiserIDs.map((id) =>
            get(ref(db, `advertisers/${id}/businessName`)).then((snap) => ({
              id,
              name: snap.val() || id,
            }))
          )
        )
      : [];

  const advertiserNameById = {};
  businessNamesMap.forEach(({ id, name }) => {
    advertiserNameById[id] = name;
  });

  const byAdvertiser = advertiserIDs
    .map((id) => ({
      id,
      name: advertiserNameById[id] || id,
      creditsSpent: advertiserCredits[id] || 0,
      videosSpent: advertiserVideos[id] || 0,
    }))
    .sort((a, b) => b.creditsSpent - a.creditsSpent);

  const byUser = userIDs
    .map((id) => ({
      id,
      email: usersMap[id] || id,
      creditsSpent: userCredits[id] || 0,
      videosSpent: userVideos[id] || 0,
    }))
    .sort((a, b) => b.creditsSpent - a.creditsSpent);

  return { byAdvertiser, byUser };
};

export const startGetDetailedCreditsUsage =
  () => async (dispatch, getState) => {
    try {
      const renewDate = getState()?.payments?.subscriptionPlanInfo?.renewDate;
      const recurrent =
        getState()?.payments?.subscriptionPlanInfo?.recurrent || "monthly";
      const typeOfPlans =
        getState()?.payments?.typeOfPlans || PLAN_TYPE_PACKAGES;

      const { agencyID } = dispatch(getPathDatabaseByRole());

      const creditSnapshot =
        getState()?.payments?.creditActionCosts ?? DEFAULT_CREDIT_ACTION_COSTS;
      const creditPricingVersion = getState()?.payments?.creditPricingVersion;
      const adsCampaignActionCostsByType = buildAdsCampaignActionCostsByType(
        creditSnapshot,
        creditPricingVersion
      );

      const { from, to } = resolveCreditsUsagePeriodBounds({
        renewDate,
        recurrent,
      });

      const { byAdvertiser, byUser } = await buildAgencyCreditsUsageBreakdown({
        agencyID,
        from,
        to,
        typeOfPlans,
        adsCampaignActionCostsByType,
      });

      const period = {
        from: format(new Date(from), "dd/MM/yyyy"),
        to: format(new Date(to), "dd/MM/yyyy"),
      };

      return { ok: true, byAdvertiser, byUser, period };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetDetailedCreditsUsage",
        })
      );
      return {
        ok: false,
        byAdvertiser: [],
        byUser: [],
        period: null,
      };
    }
  };

export const startGetSuperAdminDetailedCreditsUsage =
  () => async (dispatch) => {
    try {
      const token = await dispatch(renewToken());

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "getSuperAdminDetailedCreditsUsage"
        )}/getSuperAdminDetailedCreditsUsage`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!data?.ok) {
        return {
          ok: false,
          totalCredits: 0,
          byAgency: [],
        };
      }

      return {
        ok: true,
        totalCredits: data.totalCredits ?? 0,
        byAgency: data.byAgency ?? [],
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetSuperAdminDetailedCreditsUsage",
        })
      );
      return {
        ok: false,
        totalCredits: 0,
        byAgency: [],
      };
    }
  };
